/**
 * Solicitudes de rescate. Espejo de
 * `comparsa/src/pension-plan-withdrawals/` — el backend es la fuente de verdad
 * de estos enums.
 *
 * Recordatorio de por qué el flujo es el que es: Caser no expone ninguna
 * operación de escritura de rescates (confirmado 29-jul-2026). La plataforma
 * sólo registra la petición y avisa al equipo; el trámite ocurre por email.
 */

export enum WithdrawalStatus {
	PENDING = 'PENDING',
	CONFIRMED = 'CONFIRMED',
	REJECTED = 'REJECTED',
	CANCELLED = 'CANCELLED'
}

export enum WithdrawalKind {
	PUNCTUAL = 'PUNCTUAL',
	ANNUITY_START = 'ANNUITY_START',
	ANNUITY_UPDATE = 'ANNUITY_UPDATE',
	ANNUITY_PAUSE = 'ANNUITY_PAUSE',
	ANNUITY_RESUME = 'ANNUITY_RESUME'
}

/** Mismos `value` que `RESCUE_REASONS` en `src/utils/rescueDocsCatalog.ts`. */
export enum WithdrawalReason {
	SENIORITY = 'seniority',
	RETIREMENT = 'retirement',
	DISABILITY = 'disability',
	DEPENDENCY = 'dependency',
	SEVERE_ILLNESS = 'severe-illness',
	UNEMPLOYMENT = 'unemployment',
	DEATH = 'death'
}

export interface CreateWithdrawalRequestDto {
	/** Plan del que se rescata (`user_epp_config.id`), no el usuario. */
	userEppConfigId: string;
	kind: WithdrawalKind;
	reason: WithdrawalReason;
	/** Sólo en PUNCTUAL. */
	amount?: number;
	/** Sólo en ANNUITY_START y ANNUITY_UPDATE. */
	monthlyAmount?: number;
	metadata?: Record<string, unknown>;
}

export interface WithdrawalRequest {
	id: string;
	createdAt: string;
	updatedAt: string;
	userId: string;
	userEppConfigId: string;
	requestId: string;
	kind: WithdrawalKind;
	reason: WithdrawalReason;
	status: WithdrawalStatus;
	amount: string | null;
	monthlyAmount: string | null;
	rejectionReason: string | null;
	resolvedAt: string | null;
	metadata: Record<string, unknown> | null;
}

/**
 * La solicitud viva de un plan, si la hay.
 *
 * El backend sólo permite una `PENDING` por plan, así que como mucho
 * devuelve una. Es lo que decide si el botón de solicitar se sustituye por el
 * badge de "Pendiente".
 */
export function findOpenRequestForPlan(
	requests: WithdrawalRequest[],
	userEppConfigId: string
): WithdrawalRequest | null {
	return (
		requests.find(
			(request) =>
				request.userEppConfigId === userEppConfigId && request.status === WithdrawalStatus.PENDING
		) ?? null
	);
}

/**
 * Motivo por el que este plan ya no admite aportaciones, o null.
 *
 * SÓLO cuenta una solicitud CONFIRMADA: mientras está pendiente, la plataforma
 * se queda como está — pedir un rescate no es haberlo tramitado. Y sólo si el
 * motivo corta aportaciones: con antigüedad o enfermedad grave se sigue
 * aportando.
 */
export function findStoppingReason(requests: WithdrawalRequest[]): WithdrawalReason | null {
	return (
		requests
			.filter(
				(request) =>
					request.status === WithdrawalStatus.CONFIRMED && STOPPING_REASONS.includes(request.reason)
			)
			.sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]?.reason ?? null
	);
}

export interface AnnuityState {
	/** Importe mensual vigente, en euros. */
	monthlyAmount: number;
	paused: boolean;
}

/** Solicitudes que hablan de la renta mensual, no de un rescate puntual. */
const ANNUITY_KINDS: readonly WithdrawalKind[] = [
	WithdrawalKind.ANNUITY_START,
	WithdrawalKind.ANNUITY_UPDATE,
	WithdrawalKind.ANNUITY_PAUSE,
	WithdrawalKind.ANNUITY_RESUME
];

/** Si esta solicitud va de la renta mensual y no de un rescate puntual. */
export function isAnnuityKind(kind: WithdrawalKind): boolean {
	return ANNUITY_KINDS.includes(kind);
}

/** Sólo estas dos llevan importe: pausar y reactivar no lo cambian. */
const ANNUITY_AMOUNT_KINDS: readonly WithdrawalKind[] = [
	WithdrawalKind.ANNUITY_START,
	WithdrawalKind.ANNUITY_UPDATE
];

/**
 * Estado real de la renta mensual de un plan, o null si no hay ninguna.
 *
 * Se DERIVA de las solicitudes confirmadas, que es el único sitio donde el dato
 * existe de verdad: antes vivía en un `useState` del componente y se perdía al
 * recargar, así que se aprobaba la renta desde cuarteto y la usuaria seguía
 * viendo "inactiva" (PROD, 07-ago-2026).
 *
 * La más reciente manda para saber si está pausada; el importe lo arrastra la
 * última que lo lleva, porque pausar y reactivar no lo tocan. Sin un importe
 * válido devuelve null: una renta "activa de 0€" sería inventarse el dato.
 */
export function findAnnuityState(
	requests: WithdrawalRequest[],
	userEppConfigId: string
): AnnuityState | null {
	const annuities = requests
		.filter(
			(request) =>
				request.userEppConfigId === userEppConfigId &&
				request.status === WithdrawalStatus.CONFIRMED &&
				ANNUITY_KINDS.includes(request.kind)
		)
		.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

	const latest = annuities[0];
	if (!latest) return null;

	const monthlyAmount = Number(
		annuities.find((request) => ANNUITY_AMOUNT_KINDS.includes(request.kind))?.monthlyAmount
	);
	if (!Number.isFinite(monthlyAmount) || monthlyAmount <= 0) return null;

	return { monthlyAmount, paused: latest.kind === WithdrawalKind.ANNUITY_PAUSE };
}

/**
 * Motivos tras los que ya no se puede aportar a ESE plan. Antigüedad y
 * enfermedad grave no están: se sigue aportando después de rescatar por ellos.
 */
const STOPPING_REASONS: readonly WithdrawalReason[] = [
	WithdrawalReason.RETIREMENT,
	WithdrawalReason.DISABILITY,
	WithdrawalReason.DEPENDENCY,
	WithdrawalReason.UNEMPLOYMENT,
	WithdrawalReason.DEATH
];
